import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Search,
  Filter,
  Eye,
  Download,
  Trash2,
  Mail,
  Phone,
  Linkedin,
  Globe,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "rejected", label: "Rejected" },
  { value: "hired", label: "Hired" }
];

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    reviewed: "bg-blue-100 text-blue-800 border-blue-200",
    shortlisted: "bg-green-100 text-green-800 border-green-200",
    rejected: "bg-red-100 text-red-800 border-red-200",
    hired: "bg-purple-100 text-purple-800 border-purple-200"
  };
  return colors[status] || "bg-slate-100 text-slate-800";
};

const getStatusIcon = (status) => {
  const icons = {
    pending: Clock,
    reviewed: Eye,
    shortlisted: Star,
    rejected: XCircle,
    hired: CheckCircle
  };
  return icons[status] || Clock;
};

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [selectedApp, setSelectedApp] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [pendingStatus, setPendingStatus] = useState("");

  const defaultStatusMessages = {
    pending: "Your application has been received and is being reviewed by our team.",
    reviewed: "Your application has been reviewed. We will contact you if you're shortlisted.",
    shortlisted: "Congratulations! You have been shortlisted. Our team will contact you soon for the next steps.",
    rejected: "Thank you for your interest. Unfortunately, we have decided to move forward with other candidates.",
    hired: "Congratulations! You have been selected for this position. Welcome to the team!"
  };

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [statusFilter, jobFilter]);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      let url = `${API}/applications`;
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (jobFilter !== "all") params.append("job_id", jobFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data);
    } catch (error) {
      console.error("Error fetching applications:", error);
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const handleViewApplication = async (app) => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/applications/${app.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedApp(response.data);
      setNotes(response.data.notes || "");
      setIsViewOpen(true);
    } catch (error) {
      console.error("Error fetching application:", error);
      toast.error("Failed to load application details");
    }
  };

  const openStatusModal = (status) => {
    setPendingStatus(status);
    setStatusMessage(defaultStatusMessages[status] || "");
    setIsStatusModalOpen(true);
  };

  const updateStatus = async (appId, newStatus, customMessage = null) => {
    try {
      const token = localStorage.getItem("admin_token");
      let url = `${API}/applications/${appId}/status?status=${newStatus}&notes=${encodeURIComponent(notes)}`;
      if (customMessage) {
        url += `&status_message=${encodeURIComponent(customMessage)}`;
      }
      await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Application marked as ${newStatus}`);
      fetchApplications();
      if (selectedApp) {
        setSelectedApp({ ...selectedApp, status: newStatus, notes, status_message: customMessage });
      }
      setIsStatusModalOpen(false);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const confirmStatusUpdate = () => {
    if (selectedApp && pendingStatus) {
      updateStatus(selectedApp.id, pendingStatus, statusMessage);
    }
  };

  const downloadResume = async (app) => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/applications/${app.id}/resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const byteCharacters = atob(response.data.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Resume downloaded");
    } catch (error) {
      console.error("Error downloading resume:", error);
      toast.error("Failed to download resume");
    }
  };

  const handleDelete = (app) => {
    setSelectedApp(app);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/applications/${selectedApp.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Application deleted");
      fetchApplications();
      setIsViewOpen(false);
    } catch (error) {
      console.error("Error deleting application:", error);
      toast.error("Failed to delete application");
    } finally {
      setIsDeleteOpen(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const filteredApplications = applications.filter(app =>
    app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.job_title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Applications</h1>
        <p className="text-slate-600 mt-1">Review and manage job applications</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-applications-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={jobFilter} onValueChange={setJobFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="job-filter">
            <SelectValue placeholder="Filter by job" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Jobs</SelectItem>
            {jobs.map((job) => (
              <SelectItem key={job.id} value={job.id}>
                {job.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Applications List */}
      <div className="space-y-4">
        {filteredApplications.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No applications found</p>
            </CardContent>
          </Card>
        ) : (
          filteredApplications.map((app, index) => {
            const StatusIcon = getStatusIcon(app.status);
            return (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-[#1e3a8a] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-semibold text-lg">
                            {app.full_name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">{app.full_name}</h3>
                          <p className="text-sm text-slate-500">{app.email}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <Badge variant="outline" className="text-xs">
                              {app.job_title}
                            </Badge>
                            <Badge className={`text-xs ${getStatusColor(app.status)}`}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {app.status}
                            </Badge>
                          </div>
                          <p className="text-xs text-slate-400 mt-2">
                            Applied: {formatDate(app.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewApplication(app)}
                          data-testid={`view-app-${app.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadResume(app)}
                          data-testid={`download-resume-${app.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDelete(app)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-app-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* View Application Modal */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details</DialogTitle>
            <DialogDescription>
              Reviewing application for {selectedApp?.job_title}
            </DialogDescription>
          </DialogHeader>

          {selectedApp && (
            <div className="space-y-6 py-4">
              {/* Applicant Info */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-[#1e3a8a] rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {selectedApp.full_name?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{selectedApp.full_name}</h3>
                  <p className="text-slate-500">{selectedApp.job_title}</p>
                  <Badge className={`mt-2 ${getStatusColor(selectedApp.status)}`}>
                    {selectedApp.status}
                  </Badge>
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid sm:grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a href={`mailto:${selectedApp.email}`} className="text-[#1e3a8a] hover:underline">
                    {selectedApp.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <span>{selectedApp.phone}</span>
                </div>
                {selectedApp.linkedin && (
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-slate-400" />
                    <a href={selectedApp.linkedin} target="_blank" rel="noopener noreferrer" className="text-[#1e3a8a] hover:underline truncate">
                      LinkedIn Profile
                    </a>
                  </div>
                )}
                {selectedApp.portfolio && (
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400" />
                    <a href={selectedApp.portfolio} target="_blank" rel="noopener noreferrer" className="text-[#1e3a8a] hover:underline truncate">
                      Portfolio
                    </a>
                  </div>
                )}
              </div>

              {/* Cover Letter */}
              {selectedApp.cover_letter && (
                <div>
                  <Label className="text-slate-700 font-medium">Cover Letter</Label>
                  <div className="mt-2 p-4 bg-slate-50 rounded-lg text-slate-600 text-sm whitespace-pre-wrap">
                    {selectedApp.cover_letter}
                  </div>
                </div>
              )}

              {/* Resume */}
              <div>
                <Label className="text-slate-700 font-medium">Resume</Label>
                <div className="mt-2 flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8 text-[#1e3a8a]" />
                    <span className="text-slate-700">{selectedApp.resume_filename}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => downloadResume(selectedApp)}
                  >
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-slate-700 font-medium">Internal Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this candidate..."
                  rows={3}
                  className="mt-2"
                  data-testid="application-notes"
                />
              </div>

              {/* Tracking ID */}
              {selectedApp.tracking_id && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <span className="text-sm text-blue-700 font-medium">Tracking ID:</span>
                  <code className="bg-white px-3 py-1 rounded border border-blue-200 font-mono font-bold text-[#1e3a8a]">
                    {selectedApp.tracking_id}
                  </code>
                </div>
              )}

              {/* Status Actions */}
              <div>
                <Label className="text-slate-700 font-medium mb-3 block">Update Status</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openStatusModal("reviewed")}
                    className={selectedApp.status === "reviewed" ? "border-blue-500 bg-blue-50" : ""}
                    data-testid="status-reviewed"
                  >
                    <Eye className="w-4 h-4 mr-1" /> Reviewed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openStatusModal("shortlisted")}
                    className={selectedApp.status === "shortlisted" ? "border-green-500 bg-green-50" : ""}
                    data-testid="status-shortlisted"
                  >
                    <Star className="w-4 h-4 mr-1" /> Shortlist
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openStatusModal("rejected")}
                    className={selectedApp.status === "rejected" ? "border-red-500 bg-red-50" : ""}
                    data-testid="status-rejected"
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => openStatusModal("hired")}
                    className={selectedApp.status === "hired" ? "bg-purple-600" : "bg-[#1e3a8a] hover:bg-[#172554]"}
                    data-testid="status-hired"
                  >
                    <CheckCircle className="w-4 h-4 mr-1" /> Hire
                  </Button>
                </div>
              </div>

              <p className="text-xs text-slate-400">
                Applied on {formatDate(selectedApp.created_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={isStatusModalOpen} onOpenChange={setIsStatusModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Update Application Status
            </DialogTitle>
            <DialogDescription>
              Set the status to <strong className="text-slate-700">{pendingStatus}</strong> for {selectedApp?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 font-medium">Message to Candidate</Label>
              <p className="text-xs text-slate-500 mb-2">
                This message will be visible to the candidate when they track their application.
              </p>
              <Textarea
                value={statusMessage}
                onChange={(e) => setStatusMessage(e.target.value)}
                placeholder="Enter a message for the candidate..."
                rows={4}
                data-testid="status-message-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsStatusModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={confirmStatusUpdate}
                className="bg-[#1e3a8a] hover:bg-[#172554]"
                data-testid="confirm-status-update"
              >
                Update Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the application from {selectedApp?.full_name}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-application"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminApplications;
